// src/utils/relicVault/catalog.js
//
// Lookups over the catalog the backend serves. Everything the store and the
// vault browser display - names, prices, rarity colours, guild titles, art -
// is derived here, so a component never reaches into the raw API shape.
//
// Build the index once per catalog change (see useCollectionCatalog) rather
// than per render: the relic catalog is 675 rows.

export const ALL_GUILDS_ID = 'all';
export const NO_GUARDIAN = 'none';

/** Owned-state filter pills in the vault toolbar. */
export const OWNED_FILTERS = [
  { label: 'All', value: 'all' },
  { label: 'Owned', value: 'owned' },
  { label: 'Missing', value: 'missing' },
];

const EMPTY = Object.freeze([]);

/**
 * @param {{guilds, guardians, relics, rarities, sheetGrid, badgeCategories}} catalog
 *   as held in state.relicVault.
 */
export function buildCatalogIndex(catalog) {
  const guilds = catalog?.guilds || EMPTY;
  const guardians = catalog?.guardians || EMPTY;
  const relics = catalog?.relics || EMPTY;
  const rarities = catalog?.rarities || EMPTY;
  const badgeCategories = catalog?.badgeCategories || EMPTY;

  const guildById = new Map(guilds.map(g => [g.guildId, g]));
  const tierByKey = new Map(rarities.map(r => [r.rarity, r]));
  const relicById = new Map(relics.map(r => [r.relicId, r]));
  const guardianByGuildId = new Map(guardians.map(g => [g.guildId, g]));

  const relicsByGuild = new Map();
  for (const relic of relics) {
    const list = relicsByGuild.get(relic.guildId);
    if (list) list.push(relic);
    else relicsByGuild.set(relic.guildId, [relic]);
  }

  const badges = [];
  const badgeById = new Map();
  for (const category of badgeCategories) {
    for (const badge of category.badges || []) {
      const withCategory = { ...badge, categoryKey: category.categoryKey, categoryLabel: category.label };
      badges.push(withCategory);
      badgeById.set(badge.badgeId, withCategory);
    }
  }

  return {
    guilds,
    guardians,
    relics,
    rarities,
    badges,
    badgeCategories,
    sheetGrid: catalog?.sheetGrid || null,
    guildById,
    tierByKey,
    relicById,
    guardianByGuildId,
    relicsByGuild,
    badgeById,
    ready: relics.length > 0 || guilds.length > 0,
  };
}

export const EMPTY_CATALOG_INDEX = buildCatalogIndex(null);

// ---------------------------------------------------------------------------
// Display helpers. Each takes the index so nothing depends on module state.
// ---------------------------------------------------------------------------

export const getRelic = (index, relicId) => index.relicById.get(relicId);
export const guildById = (index, guildId) => index.guildById.get(guildId);
export const tierByKey = (index, rarity) => index.tierByKey.get(rarity);
export const getBadge = (index, badgeId) => index.badgeById.get(badgeId);
export const getGuardian = (index, guildId) => index.guardianByGuildId.get(guildId);

export const guildLabelFor = (index, relic) =>
  index.guildById.get(relic?.guildId)?.title || '';

export const rarityColorFor = (index, relic) =>
  index.tierByKey.get(relic?.rarity)?.colorHex || '#8B5CF6';

export const rarityLabelFor = (index, relic) =>
  index.tierByKey.get(relic?.rarity)?.label || relic?.rarity || '';

/** Name shown for a relic the learner has not discovered. */
export const undiscoveredName = (index, relic) =>
  `Undiscovered ${rarityLabelFor(index, relic)} Relic`;

/**
 * Vault detail copy: the real description once owned, otherwise the tier's own
 * locked hint, which the backend serves alongside the rarity.
 */
export function detailDescriptionFor(index, relic) {
  if (relic?.owned) return relic.description;
  return (
    index.tierByKey.get(relic?.rarity)?.lockedHint ||
    'Keep exploring to discover this relic.'
  );
}

/**
 * Where a relic's icon sits in its sprite sheet, as a background style. The
 * sheet is a grid (3x2 today) and each relic names its cell, so one image
 * serves six icons.
 */
export function spriteStyleFor(index, relic, size) {
  if (!relic?.sheetUrl || !index.sheetGrid) return null;
  const { columns, rows } = index.sheetGrid;
  return {
    backgroundImage: `url(${relic.sheetUrl})`,
    backgroundSize: `${columns * 100}% ${rows * 100}%`,
    // Percentage positioning places cell n at n/(count-1) of the travel.
    backgroundPosition: `${(relic.col / (columns - 1)) * 100}% ${
      (relic.row / (rows - 1)) * 100
    }%`,
    backgroundRepeat: 'no-repeat',
    width: size,
    height: size,
  };
}

// ---------------------------------------------------------------------------
// Vault browser derivations.
// ---------------------------------------------------------------------------

/** Relics for a guild (or every guild), each tagged with `owned`. */
export function relicsForGuild(index, guildId, ownedRelicIds) {
  const ownedSet = new Set(ownedRelicIds);
  const pool =
    guildId === ALL_GUILDS_ID ? index.relics : index.relicsByGuild.get(guildId) || EMPTY;
  return pool.map(r => ({ ...r, owned: ownedSet.has(r.relicId) }));
}

/**
 * Discovered relics first, in catalog order, then the undiscovered ones by
 * name. What the learner owns is what they came to look at, and the locked
 * remainder reads as a list to hunt through rather than a shuffled wall.
 *
 * Array.prototype.sort is stable, so returning 0 leaves the owned run in the
 * order the catalog gave it.
 */
export function compareOwnedFirst(a, b) {
  if (a.owned !== b.owned) return a.owned ? -1 : 1;
  if (a.owned) return 0;
  return a.name.localeCompare(b.name);
}

export function passesFilters(relic, query, ownedFilter) {
  if (query && !relic.name.toLowerCase().includes(query.toLowerCase())) return false;
  if (ownedFilter === 'owned' && !relic.owned) return false;
  if (ownedFilter === 'missing' && relic.owned) return false;
  return true;
}

/**
 * One entry per rarity tier for the selected guild. Mythic renders as a feature
 * row rather than a grid whenever a specific guild is selected.
 *
 * Owned/total counts ignore the search and owned filters, so the section header
 * always reflects real progress.
 */
export function buildRaritySections(index, currentRelics, selectedGuildId, query, ownedFilter) {
  return index.rarities.map(tier => {
    const tierAll = currentRelics.filter(r => r.rarity === tier.rarity);
    return {
      key: tier.rarity,
      label: tier.label,
      color: tier.colorHex,
      owned: tierAll.filter(r => r.owned).length,
      total: tierAll.length,
      isMythicFeature: tier.rarity === 'mythic' && selectedGuildId !== ALL_GUILDS_ID,
      items: tierAll
        .filter(r => passesFilters(r, query, ownedFilter))
        .sort(compareOwnedFirst),
    };
  });
}

/** Owned / total / percent for the vault header progress bar and rarity chips. */
export function summarizeProgress(index, currentRelics) {
  const ownedTotal = currentRelics.filter(r => r.owned).length;
  const total = currentRelics.length;
  return {
    ownedTotal,
    total,
    pct: total ? Math.round((ownedTotal / total) * 100) : 0,
    tiers: index.rarities.map(t => {
      const items = currentRelics.filter(r => r.rarity === t.rarity);
      return {
        key: t.rarity,
        label: t.label,
        color: t.colorHex,
        owned: items.filter(r => r.owned).length,
        total: items.length,
      };
    }),
  };
}
